<script setup lang="ts">
import { avatarText } from '@core/utils/formatters'
import { $getChatMessages } from '@/api/global-chat'
import type { ChatMessage} from '@/models/global/GlobalChat'
import { useUserStore } from '@/store/user'
import type { TaggedUser } from '@/models/activity-timeline'

interface IProps {
  modelValue?: boolean,
  itemId?: number | string | Array<number | string> | null,
  itemType?: 'general' | 'wayfinding' | 'branding' | 'kop' | 'change_request' | 'change_request_approval' |
    'change_request_subtask' | 'finance_manual_entry' | 'waiting_room_file' |
    'file_management_file' | 'file_management_in_waiting_room_file',
  customMessageInputId?: string,
  canAddComment?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  itemId: null,
  itemType: 'general',
  customMessageInputId: 'activity-timeline-comment-custom-input',
  canAddComment: true,
})

interface CustomEvents {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onMessageAdded'): void
}

const emit = defineEmits<CustomEvents>()

const userStore = useUserStore()

const chatMessages = ref<ChatMessage[]>([])
const chatMessagesLoading = ref<boolean>(false)
const chatMessagesCount = ref<number>(0)
const currentPage = ref<number>(1)
const perPage = ref<number>(20)
const isLoadingOlderMessages = ref<boolean>(false)
const hasMoreMessages = ref<boolean>(true)

const dialogWidth = ref(920)
const dialogHeight = ref(500)

const isBulkChatMode = computed<boolean>(() => {
  return Array.isArray(props.itemId) && props.itemId.length > 1
})

const selectedItemsCount = computed<number>(() => {
  return Array.isArray(props.itemId) ? props.itemId.length : (props.itemId ? 1 : 0)
})

const primaryChatItemId = computed<number | string | null>(() => {
  if (Array.isArray(props.itemId)) {
    return props.itemId.length ? props.itemId[0] : null
  }
  return props.itemId ?? null
})

// Function to update dialog size based on screen size
const updateDialogSize = () => {
  const maxWidth = 920
  const maxHeight = 930

  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight

  // Update width and height with constraints for max size
  dialogWidth.value = Math.min(screenWidth * 0.9, maxWidth)  // 90% of screen width or maxWidth
  dialogHeight.value = Math.min(screenHeight * 0.9, maxHeight)  // 90% of screen height or maxHeight
}

const getChatMessagesFilters = () => {
  const messageTypeMap = {
    general: 1,
    wayfinding: 2,
    branding: 3,
    kop: 4,
    change_request: 5,
    change_request_approval: 6,
    change_request_subtask: 7,
    finance_manual_entry: 8,
    waiting_room_file: 9,
    file_management_file: 10,
    file_management_in_waiting_room_file: 11,
  }

  const foreignKeyMap = {
    wayfinding: 'sign',
    branding: 'line_item',
    kop: 'kop_item',
    change_request: 'change_request',
    change_request_approval: 'change_request_approval',
    change_request_subtask: 'change_request_subtask',
    finance_manual_entry: 'finance_manual_entry',
    waiting_room_file: 'waiting_room_file',
    file_management_file: 'file_management_file',
    file_management_in_waiting_room_file: 'file_management_file',
  }

  const payload = {
    reverse: true,  // Reversed pages for correct UI display
    // Chat with status changes (approve/reject/move)
    is_chat_with_history: ['wayfinding', 'branding', 'kop'].includes(props.itemType) ? true : undefined,
    message_type: messageTypeMap[props.itemType],
    page: currentPage.value,
    // per_page: perPage.value
  } as ChatMessage

  if (props.itemType !== 'general' && foreignKeyMap[props.itemType]) {
    const itemType = foreignKeyMap[props.itemType] as
      'sign' | 'line_item' | 'kop_item' | 'change_request' | 'change_request_approval' | 'change_request_subtask' |
      'finance_manual_entry' | 'waiting_room_file' | 'file_management_file'
    payload[itemType] = primaryChatItemId.value as never
  }

  return payload
}

const getChatMessages = async (loadMore: boolean = false) => {
  if (isBulkChatMode.value) {
    chatMessages.value = []
    chatMessagesCount.value = 0
    return
  }

  if (props.itemType !== 'general' && !primaryChatItemId.value) {
    chatMessages.value = []
    chatMessagesCount.value = 0
    return
  }

  if (loadMore) {
    if (!hasMoreMessages.value || isLoadingOlderMessages.value) return
    isLoadingOlderMessages.value = true
    currentPage.value += 1
  } else {
    chatMessagesLoading.value = true
    currentPage.value = 1
    hasMoreMessages.value = true
  }

  const filters = getChatMessagesFilters()

  try {
    const res = await $getChatMessages(filters)

    if (loadMore) {
      // Prepend older messages to the array
      chatMessages.value = [...res.data.results, ...chatMessages.value]
    } else {
      chatMessages.value = res.data.results
    }

    chatMessagesCount.value = res.data.count
    hasMoreMessages.value = chatMessages.value.length < chatMessagesCount.value

    if (!loadMore) {
      scrollToBottom()
    }
  } catch (e) {
    console.error('Error loading messages:', e)
    // Roll back page increment if error occurred during load more
    if (loadMore) {
      currentPage.value = Math.max(1, currentPage.value - 1)
    }
  } finally {
    if (loadMore) {
      isLoadingOlderMessages.value = false
    } else {
      chatMessagesLoading.value = false
    }
  }
}

const handleScroll = (e: Event) => {
  if (isBulkChatMode.value) return

  const container = e.target as HTMLElement
  if (!container) return

  // Load more when scrolled near the top (within 200px)
  if (container.scrollTop < 200 && hasMoreMessages.value && !isLoadingOlderMessages.value) {
    getChatMessages(true)
  }
}

const closeDialog = () => {
  currentPage.value = 1
  emit('update:modelValue', !props.modelValue)
}

const onClose = async () => {
  emit('onClose')
}

watch(() => props.modelValue, () => {
  if (props.modelValue) {
    currentPage.value = 1
    if (isBulkChatMode.value) {
      chatMessages.value = []
      chatMessagesCount.value = 0
    } else {
      getChatMessages()
    }
  }
})

const isCurrentUser = (msg: ChatMessage) => {
  return msg?.created_by === userStore?.userId
}

const formatTime = (dateString?: string) => {
  return dateString || ''
}

const handleNewMessage = () => {
  if (!isBulkChatMode.value) {
    getChatMessages()
  }
  emit('onMessageAdded')
}

const scrollToBottom = async () => {
  await nextTick()

  const container = document.getElementById('chat-messages-card')

  if (container) {
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight
    })

    setTimeout(() => {
      container.scrollTop = container.scrollHeight
    }, 50)
  }
}

const getMessageAvatarText = (createdByDisplay?: string | null) => {
  if (!createdByDisplay) {
    return ''
  }

  const createdByDisplayArr = createdByDisplay.split(' ')

  if (!Array.isArray(createdByDisplayArr) || createdByDisplayArr.length === 0) {
    return ''
  }
  const firstTwo = createdByDisplayArr.slice(0, 2)

  return avatarText(firstTwo.filter((item: string) => item != null).join(' '))
}

// Add event listener for window resize and update dialog size accordingly
onMounted(() => {
  updateDialogSize() // Initialize dialog size when component mounts
  window.addEventListener('resize', updateDialogSize)
})

// Cleanup event listener when component unmounts
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDialogSize)
})

// Function to highlight tagged users in activityTimelineItem content
const highlightTaggedUsers = (
  content?: string | null,
  taggedUsers: TaggedUser[] = [],
  messageId?: string | number
): string => {
  if (!content) return ''

  const normalizedMessageId = String(messageId ?? '')

  // Sort by ID descending length so @1841 is replaced before @1
  const sortedUsers = [...taggedUsers].sort(
    (a, b) => String(b.id).length - String(a.id).length
  )

  return sortedUsers.reduce((result, taggedUser) => {
    const fullName = [taggedUser.first_name, taggedUser.last_name]
      .filter(Boolean)
      .join(' ') || taggedUser.email

    return result.replaceAll(
      `@${taggedUser.id}`,
      `<span class="highlight-chat-tag-user chat-tag-user-${normalizedMessageId}-${taggedUser.id}">@${fullName}</span>`
    )
  }, content)
}

const getMessageTaggedUsers = (msg: ChatMessage): TaggedUser[] => {
  return (msg.tagged_users || []) as unknown as TaggedUser[]
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="95vw"
    max-height="95vh"
    :width="dialogWidth"
    :height="dialogHeight"
    class="chat-dialog"
    @after-leave="onClose"
    @update:modelValue="(newValue: boolean) => emit('update:modelValue', newValue)"
  >
    <!-- Resize handle component -->
    <DialogResizeHandle
      v-model:dialogWidth="dialogWidth"
      v-model:dialogHeight="dialogHeight"
      :minWidth="300"
      :minHeight="200"
    />

    <VCard class="d-flex flex-column h-100">
      <!-- Header -->
      <VCardTitle class="global-chat-dialog-card-title vss-movable">
        <MovableDialog />

        <span class="text-h5 text-white">Chat</span>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          color="white"
          @click="closeDialog"
        />
      </VCardTitle>

      <!-- Chat Messages Area - Fixed height container -->
      <div class="messages-wrapper">
        <VCardText
          id="chat-messages-card"
          class="chat-messages pa-4"
          @scroll="handleScroll"
        >
          <div
            v-if="isBulkChatMode"
            class="bulk-chat-info"
          >
            <VIcon
              size="56"
              color="primary"
            >
              mdi-format-list-bulleted-square
            </VIcon>
            <p class="text-h6 mt-3 mb-1">
              Bulk Note Mode
            </p>
            <p class="text-body-2 text-medium-emphasis text-center mb-0">
              You selected {{ selectedItemsCount }} item(s).<br>
              Your message will be added to all selected items.
            </p>
          </div>
          <div
            v-else
            id="chat-messages-container"
            class="messages-container"
            :class="{ 'empty-state': !chatMessages.length }"
          >
            <!-- Loading indicator for older messages -->
            <div v-if="isLoadingOlderMessages" class="text-center py-4">
              <VProgressCircular indeterminate size="24" />
            </div>

            <template v-if="chatMessages.length">
              <div
                v-for="msg in chatMessages"
                :key="msg.id"
                class="message-wrapper"
              >
                <!-- History Messages -->
                <div v-if="msg.is_history" class="history-message">
                  <div class="history-pill">
                    {{ msg.content }}
                    <div class="history-pill-created-display mt-1">
                      {{ formatTime(msg.created_display) }}
                    </div>
                  </div>
                </div>

                <!-- Regular Chat Messages -->
                <div
                  v-else
                  class="message-bubble mb-4"
                  :class="{
                  'message-own': isCurrentUser(msg),
                  'message-other': !isCurrentUser(msg)
                }"
                >
                  <div class="message-header mb-1">
                    <VAvatar
                      size="28"
                      class="mr-2"
                    >
                      <span
                        class="text-sm-center font-weight-medium"
                      >
                        {{ getMessageAvatarText(msg.created_by_display) }}
                      </span>
                    </VAvatar>
                    <strong>{{ msg.created_by_display || 'Anonymous' }}</strong>
                    <span class="text-caption text-medium-emphasis ml-2">
                      {{ formatTime(msg.created_display) }}
                    </span>
                  </div>
                  <div class="message-content">
                    <div
                      v-html="highlightTaggedUsers(msg.content, getMessageTaggedUsers(msg), msg.id)"
                    />
                    <VMenu
                      v-for="(taggedUser, taggedUserKey) in getMessageTaggedUsers(msg)"
                      :key="taggedUserKey"
                      :activator="`.chat-tag-user-${msg.id}-${taggedUser.id}`"
                      location="top center"
                      open-on-hover
                      open-delay="100"
                      close-delay="0"
                      offset="5"
                      class="default-tooltip"
                    >
                      <span class="px-2 text-center">
                        {{ taggedUser.email }}
                      </span>
                    </VMenu>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="empty-messages">
              <VIcon size="64" color="grey-lighten-1">mdi-forum-outline</VIcon>
              <p class="text-h6 text-grey mt-2">No messages yet</p>
              <p class="text-grey">Be the first to start the conversation!</p>
            </div>
          </div>
        </VCardText>
      </div>

      <!-- Input Area -->
      <div class="input-wrapper">
        <CommentTagInput
          :is-global-chat="true"
          :chat-item-id="itemId"
          :chat-item-type="itemType"
          :custom-message-input-id="customMessageInputId"
          :can-add-comment="canAddComment"
          placeholder="Type your message..."
          class="input-content"
          @onAddComment="handleNewMessage"
        />
      </div>
    </VCard>
  </VDialog>
</template>

<style lang="scss">
.chat-dialog {
  overflow: hidden;
  .global-chat-dialog-card-title {
    --v-theme-overlay-multiplier: var(--v-theme-primary-overlay-multiplier);
    background-color: rgb(var(--v-theme-primary)) !important;
    color: rgb(var(--v-theme-on-primary)) !important;

    padding-top: 12px !important;
    padding-bottom: 12px !important;
    display: flex !important;
    align-items: center !important;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.85) !important;
    }
  }

  .v-overlay__content {
    max-height: 95vh;
  }

  // Main card container
  .v-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  // Messages wrapper - takes remaining space minus input height
  .messages-wrapper {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  // Actual messages container with scroll
  .chat-messages {
    flex: 1;
    overflow-y: auto;
    background-color: rgb(var(--v-theme-surface));
    border-top: 1px solid rgba(var(--v-theme-surface), 0.5);
    border-bottom: 1px solid rgba(var(--v-theme-surface), 0.5);
    padding-bottom: 0 !important;

    .messages-container {
      min-height: min-content;
      display: flex;
      flex-direction: column;
      padding-bottom: 16px;
    }
  }

  // Input area - absolutely positioned
  .input-wrapper {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgb(var(--v-theme-surface));
    z-index: 10;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
    border-top: 1px solid rgba(0, 0, 0, 0.1);

    .input-content {
      padding: 12px 16px;
      background: rgb(var(--v-theme-surface));
    }
  }

  // Message styling
  .message-bubble {
    max-width: 80%;
    padding: 12px 16px;
    border-radius: 18px;
    position: relative;
    transition: all 0.3s ease;
    word-break: break-word;
  }

  .message-own {
    align-self: flex-end;
    background-color: rgba(var(--v-theme-primary));
    color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    border-bottom-right-radius: 4px;

    .highlight-chat-tag-user {
      cursor: pointer;
      color: #f3cbcb !important;
      &:hover {
        text-decoration: underline;
      }
    }

    .message-header {
      color: rgba(255, 255, 255, 0.9);
    }
    .v-avatar {
      color: rgba(var(--v-theme-primary));
      background-color: #fff;
    }
  }

  .message-other {
    align-self: flex-start;
    background-color: rgba(173, 172, 172, 0.2);
    color: #333;
    color: rgba(var(--v-theme-on-surface), 1) !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    border-bottom-left-radius: 4px;

    .highlight-chat-tag-user {
      cursor: pointer;
      color: rgba(var(--v-theme-primary)) !important;
      &:hover {
        text-decoration: underline;
      }
    }

    .v-avatar {
      color: #FFFFFF;
      background-color: rgba(var(--v-theme-primary));
    }
  }

  .message-header {
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    margin-bottom: 4px;
  }

  .message-content {
    line-height: 1.5;
  }

  .empty-messages {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 40px 0;
    text-align: center;
  }

  .bulk-chat-info {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 40px 20px;
    border: 1px dashed rgba(var(--v-theme-primary), 0.3);
    border-radius: 12px;
    background: rgba(var(--v-theme-primary), 0.04);
  }
}

.message-wrapper {
  min-height: min-content;
  display: flex;
  flex-direction: column;
}

.history-message {
  text-align: center;
  margin: 10px auto;
  max-width: 60%;
  @media (max-width: 960px) {
    max-width: 80%;
  }
  .history-pill {
    background-color: #e0e0e0;
    color: #616161;
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 0.7rem;
    display: inline-block;
  }
  .history-pill-created-display {
    font-size: 0.6rem;
    font-weight: 400;
    line-height: 0.7rem;
  }
}
</style>
